'use client';

import { useState, useEffect, useCallback } from 'react';
import './ControlsPanel.scss';
import { ControlDefinition, NumericControlDefinition, FileControlDefinition, ControlValues } from '@/types/api';
import { RatingStats, RatingForm, CommentsSection } from '@/app/components';
import { Sparkles } from 'lucide-react';

interface ControlsPanelProps {
  // Controls management
  controls?: ControlDefinition[];
  onControlsChange?: (controls: ControlDefinition[]) => void;
  controlValues?: ControlValues;
  onControlValuesChange?: (values: ControlValues) => void;
  canEdit?: boolean;
  isExecuting?: boolean;
  lastExecutionTime?: number;
  avgExecutionTime?: number;
  autoUpdate?: boolean;
  onAutoUpdateChange?: (enabled: boolean) => void;
  // Description
  description?: string;
  onDescriptionChange?: (description: string) => void;
  // Social features
  sketchId?: string;
  showSocialFeatures?: boolean;
  // AI Assistant
  aiEnabled?: boolean;
  onAiClick?: () => void;
}

export default function ControlsPanel({
  controls = [],
  onControlsChange,
  controlValues = {},
  onControlValuesChange,
  canEdit = false,
  isExecuting = false,
  lastExecutionTime = 0,
  avgExecutionTime = 0,
  autoUpdate = false,
  onAutoUpdateChange,
  description = '',
  onDescriptionChange,
  sketchId,
  showSocialFeatures = false,
  aiEnabled = false,
  onAiClick,
}: ControlsPanelProps) {
  const [editingControl, setEditingControl] = useState<ControlDefinition | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [ratingStats, setRatingStats] = useState({
    totalRatings: 0,
    avgStars: 0,
    successCount: 0,
    successRate: 0,
  });

  // Load rating stats when sketch ID is available
  const loadRatingStats = useCallback(async () => {
    if (!sketchId) return;

    try {
      const response = await fetch(`/api/sketches/${sketchId}/ratings`);
      const data = await response.json();

      if (data.success && data.stats) {
        setRatingStats(data.stats);
      }
    } catch (err) {
      console.error('Error loading rating stats:', err);
    }
  }, [sketchId]);

  // Load stats on mount if sketch ID is available
  useEffect(() => {
    if (showSocialFeatures && sketchId) {
      loadRatingStats();
    }
  }, [showSocialFeatures, sketchId, loadRatingStats]);

  const handleAddControl = () => {
    const newControl: NumericControlDefinition = {
      type: 'numeric',
      name: `control_${controls.length + 1}`,
      label: `Control ${controls.length + 1}`,
      min: 0,
      max: 100,
      default: 50,
      step: 1,
    };
    setEditingControl(newControl);
    setShowAddForm(true);
  };

  const handleSaveControl = () => {
    if (!editingControl || !onControlsChange) return;

    const existingIndex = controls.findIndex(c => c.name === editingControl.name);
    if (existingIndex >= 0) {
      const newControls = [...controls];
      newControls[existingIndex] = editingControl;
      onControlsChange(newControls);
    } else {
      onControlsChange([...controls, editingControl]);
    }

    setEditingControl(null);
    setShowAddForm(false);
  };

  const handleEditControl = (control: ControlDefinition) => {
    setEditingControl({ ...control });
    setShowAddForm(true);
  };

  const handleDeleteControl = (name: string) => {
    if (onControlsChange) {
      onControlsChange(controls.filter(c => c.name !== name));
    }
  };

  const handleCancelEdit = () => {
    setEditingControl(null);
    setShowAddForm(false);
  };

  const handleControlValueChange = (name: string, value: any) => {
    if (onControlValuesChange) {
      onControlValuesChange({ ...controlValues, [name]: value });
    }
  };

  const handleFileChange = (name: string, files: FileList | null) => {
    if (files && files.length > 0 && onControlValuesChange) {
      const file = files[0];
      onControlValuesChange({ ...controlValues, [name]: file });
    }
  };

  const handleTypeChange = (type: 'numeric' | 'file') => {
    if (!editingControl) return;

    if (type === 'numeric') {
      setEditingControl({
        type: 'numeric',
        name: editingControl.name,
        label: editingControl.label,
        min: 0,
        max: 100,
        default: 50,
        step: 1,
      });
    } else {
      setEditingControl({
        type: 'file',
        name: editingControl.name,
        label: editingControl.label,
        allowedExtensions: '.stl,.obj',
        defaultPath: '',
      });
    }
  };

  return (
    <div className="controls-panel">
      <div className="panel-header">
        <div className="panel-title">
          <h3>
            Controls
            {aiEnabled && onAiClick && (
              <button
                className="ai-assistant-btn"
                onClick={onAiClick}
                title="Open AI Assistant"
              >
                <Sparkles size={16} />
                AI
              </button>
            )}
          </h3>
        </div>
      </div>
      <div className="controls-content">
        {/* Description Section */}
        {(canEdit || description) && (
          <>
            <h4>Description</h4>
            {canEdit ? (
              <div className="control-group">
                <textarea
                  className="description-textarea"
                  value={description}
                  onChange={(e) => onDescriptionChange?.(e.target.value)}
                  placeholder="Add a description for your sketch..."
                  rows={3}
                />
              </div>
            ) : description ? (
              <div className="control-group">
                <p className="description-text">{description}</p>
              </div>
            ) : null}
            <div style={{ borderTop: '1px solid #3e3e3e', marginTop: '16px', marginBottom: '16px' }} />
          </>
        )}



        {/* Interactive Controls Section */}
        {controls.length > 0 && (
          <>
            <h4 style={{ marginTop: '16px', borderTop: '1px solid #3e3e3e', paddingTop: '12px' }}>
              Interactive Parameters
            </h4>
            {onAutoUpdateChange && (
              <div className="control-group" style={{ marginBottom: '12px' }}>
                <label>
                  <input
                    type="checkbox"
                    checked={autoUpdate}
                    onChange={(e) => onAutoUpdateChange(e.target.checked)}
                    style={{ marginRight: '8px' }}
                  />
                  Auto-update visualization
                </label>
              </div>
            )}
            {controls.map((control) => {
              if (control.type === 'numeric') {
                const currentValue = controlValues[control.name] ?? control.default;
                return (
                  <div key={control.name} className="interactive-control">
                    <div className="control-header">
                      <label>{control.label}</label>
                      {canEdit && onControlsChange && (
                        <div className="control-actions-inline">
                          <button onClick={() => handleEditControl(control)} className="edit-btn-small">
                            ✏️
                          </button>
                          <button onClick={() => handleDeleteControl(control.name)} className="delete-btn-small">
                            🗑️
                          </button>
                        </div>
                      )}
                    </div>
                    <div className="control-value-input">
                      <input
                        type="number"
                        min={control.min}
                        max={control.max}
                        step={control.step || 1}
                        value={currentValue}
                        onChange={(e) => handleControlValueChange(control.name, parseFloat(e.target.value))}
                        disabled={isExecuting}
                      />
                    </div>
                    <input
                      type="range"
                      min={control.min}
                      max={control.max}
                      step={control.step || 1}
                      value={currentValue}
                      onChange={(e) => handleControlValueChange(control.name, parseFloat(e.target.value))}
                      disabled={isExecuting}
                      className="control-slider"
                    />
                    <div className="range-labels">
                      <span>{control.min}</span>
                      <span>{control.max}</span>
                    </div>
                  </div>
                );
              } else if (control.type === 'file') {
                return (
                  <div key={control.name} className="interactive-control">
                    <div className="control-header">
                      <label>{control.label}</label>
                      {canEdit && onControlsChange && (
                        <div className="control-actions-inline">
                          <button onClick={() => handleEditControl(control)} className="edit-btn-small">
                            ✏️
                          </button>
                          <button onClick={() => handleDeleteControl(control.name)} className="delete-btn-small">
                            🗑️
                          </button>
                        </div>
                      )}
                    </div>
                    <div className="control-value-input">
                      <input
                        type="file"
                        accept={control.allowedExtensions}
                        onChange={(e) => handleFileChange(control.name, e.target.files)}
                        disabled={isExecuting}
                        style={{ width: '100%' }}
                      />
                      {controlValues[control.name] && (
                        <div style={{ fontSize: '0.8em', marginTop: '4px', color: '#666' }}>
                          Selected: {controlValues[control.name] instanceof File ? (controlValues[control.name] as File).name : String(controlValues[control.name])}
                        </div>
                      )}
                    </div>
                  </div>
                );
              }
              return null;
            })}
          </>
        )}

        {/* Control Definition Section (Edit Mode Only) */}
        {canEdit && onControlsChange && (
          <>
            <h4 style={{ marginTop: '16px', borderTop: '1px solid #3e3e3e', paddingTop: '12px' }}>
              Control Definitions
            </h4>

            {showAddForm && editingControl && (
              <div className="control-form">
                <h5>{controls.some(c => c.name === editingControl.name) ? 'Edit' : 'Add'} Control</h5>

                <div className="form-group">
                  <label>Type:</label>
                  <select
                    value={editingControl.type}
                    onChange={(e) => handleTypeChange(e.target.value as 'numeric' | 'file')}
                  >
                    <option value="numeric">Numeric Slider</option>
                    <option value="file">File Import</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Variable Name:</label>
                  <input
                    type="text"
                    value={editingControl.name}
                    onChange={(e) => setEditingControl({ ...editingControl, name: e.target.value })}
                    placeholder="e.g., side_length"
                  />
                </div>

                <div className="form-group">
                  <label>Display Label:</label>
                  <input
                    type="text"
                    value={editingControl.label}
                    onChange={(e) => setEditingControl({ ...editingControl, label: e.target.value })}
                    placeholder="e.g., Side Length"
                  />
                </div>

                {editingControl.type === 'numeric' && (
                  <>
                    <div className="form-row">
                      <div className="form-group">
                        <label>Min:</label>
                        <input
                          type="number"
                          value={editingControl.min}
                          onChange={(e) => setEditingControl({ ...editingControl, min: parseFloat(e.target.value) })}
                        />
                      </div>

                      <div className="form-group">
                        <label>Max:</label>
                        <input
                          type="number"
                          value={editingControl.max}
                          onChange={(e) => setEditingControl({ ...editingControl, max: parseFloat(e.target.value) })}
                        />
                      </div>
                    </div>

                    <div className="form-row">
                      <div className="form-group">
                        <label>Default:</label>
                        <input
                          type="number"
                          value={editingControl.default}
                          onChange={(e) => setEditingControl({ ...editingControl, default: parseFloat(e.target.value) })}
                        />
                      </div>

                      <div className="form-group">
                        <label>Step:</label>
                        <input
                          type="number"
                          value={editingControl.step || ''}
                          onChange={(e) => setEditingControl({ ...editingControl, step: e.target.value ? parseFloat(e.target.value) : undefined })}
                          placeholder="1"
                        />
                      </div>
                    </div>
                  </>
                )}

                {editingControl.type === 'file' && (
                  <>
                    <div className="form-group">
                      <label>Allowed Extensions (comma separated):</label>
                      <input
                        type="text"
                        value={editingControl.allowedExtensions || ''}
                        onChange={(e) => setEditingControl({ ...editingControl, allowedExtensions: e.target.value })}
                        placeholder=".stl, .obj, .png"
                      />
                    </div>
                    <div className="form-group">
                      <label>Default Path (Desktop only):</label>
                      <input
                        type="text"
                        value={editingControl.defaultPath || ''}
                        onChange={(e) => setEditingControl({ ...editingControl, defaultPath: e.target.value })}
                        placeholder="C:/path/to/default.stl"
                      />
                    </div>
                  </>
                )}

                <div className="form-actions">
                  <button onClick={handleSaveControl} className="save-btn">
                    Save
                  </button>
                  <button onClick={handleCancelEdit} className="cancel-btn">
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {!showAddForm && (
              <button onClick={handleAddControl} className="add-control-btn">
                + Add Control
              </button>
            )}

            {controls.length > 0 && !showAddForm && (
              <div className="help-text">
                <p>
                  💡 Controls let viewers adjust parameters in presentation mode.
                  Variable names must match your code exactly.
                </p>
              </div>
            )}
          </>
        )}

        {/* Social Features Section */}
        {showSocialFeatures && sketchId && (
          <>
            <div style={{ borderTop: '1px solid #3e3e3e', marginTop: '16px', marginBottom: '16px' }} />

            <h4 style={{ marginBottom: '12px' }}>Community</h4>

            <RatingStats
              totalRatings={ratingStats.totalRatings}
              avgStars={ratingStats.avgStars}
              successCount={ratingStats.successCount}
              successRate={ratingStats.successRate}
            />

            <RatingForm
              sketchId={sketchId}
              currentControlSettings={controlValues}
              onRatingSubmitted={loadRatingStats}
            />

            <CommentsSection sketchId={sketchId} />
          </>
        )}
      </div>
    </div>
  );
}
